import React, { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import { BiChevronDown, BiChevronUp, BiPlus, BiTargetLock, BiFilterAlt } from 'react-icons/bi';
import { Fab, Action } from 'react-tiny-fab';
import { TitleContainer } from '../../DRE/RealizadoXRealizado/web/styles.ts';
import { PageContainer, AccordionContainer, HotelHeader, HotelName, MetaInfo, AccordionContent } from './styles.ts';
import { HotelForecast, FilterParams, ForecastData } from './types.ts';
import ForecastModal from './components/forecastModal.tsx';
import { Grid, Paper, FormControl, InputLabel, Select, MenuItem, TextField, Button, Box, Typography, Autocomplete, Collapse, IconButton } from '@mui/material';
import { meses } from '../../../helpers/months.ts';
import { getForecastsByAnoMes, createForecast } from '../../../services/forecastRevenueManager/forecastRevenueManager.ts';
import { formatarValor } from '../../../helpers/formatarValor.ts';

interface UnitOption {
  label: string;
  value: string;
}

const LancamentoForecast: React.FC = () => {
  const dispatch = useDispatch();
  const [cookies] = useCookies(['usuario']);
  const [hotels, setHotels] = useState<HotelForecast[]>([]);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [showForecastModal, setShowForecastModal] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [unitOptions, setUnitOptions] = useState<UnitOption[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<UnitOption | null>(null);

  const [filters, setFilters] = useState<FilterParams>({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    startMonth: new Date().getMonth() + 1,
    endMonth: new Date().getMonth() + 1,
  });

  const toggleAccordion = (hotelId: string): void => {
    setActiveKeys((prevKeys) => (prevKeys.includes(hotelId) ? prevKeys.filter((key) => key !== hotelId) : [...prevKeys, hotelId]));
  };

  const loadData = async (): Promise<void> => {
    dispatch({
      type: 'OPEN_LOADING',
      text: 'Carregando dados...',
      isLoading: true,
    });

    try {
      const result = await getForecastsByAnoMes(cookies, filters.year, filters.month, filters.unidade_id === '' ? null : filters.unidade_id);

      if (result && result.forecasts) {
        const mappedHotels = result.forecasts.map((forecast: any) => {
          let hotelUnit = unitOptions.find((unit) => unit.value === forecast.unidade_id);
          if (!hotelUnit && unitOptions.length > 0) {
            const paraceSerNome = isNaN(Number(forecast.unidade_id)) || !forecast.unidade_id.startsWith('H');

            if (paraceSerNome) {
              hotelUnit = unitOptions.find((unit) => unit.label.toLowerCase() === forecast.unidade_id.toLowerCase());

              if (!hotelUnit) {
                hotelUnit = unitOptions.find((unit) => unit.label.toLowerCase().includes(forecast.unidade_id.toLowerCase()) || forecast.unidade_id.toLowerCase().includes(unit.label.toLowerCase()));
              }
            }
          }

          if (!hotelUnit) {
            try {
              const unidadesFromStorage = JSON.parse(sessionStorage.getItem('unidades') || '[]');

              let matchingUnit = unidadesFromStorage.find((unit: any) => unit.unidade_id === forecast.unidade_id);

              if (!matchingUnit) {
                matchingUnit = unidadesFromStorage.find(
                  (unit: any) =>
                    unit.unidade.toLowerCase() === forecast.unidade_id.toLowerCase() ||
                    unit.unidade.toLowerCase().includes(forecast.unidade_id.toLowerCase()) ||
                    forecast.unidade_id.toLowerCase().includes(unit.unidade.toLowerCase())
                );
              }

              if (matchingUnit) {
                hotelUnit = {
                  label: matchingUnit.unidade,
                  value: matchingUnit.unidade_id,
                };
              }
            } catch (error) {
              console.error('Erro ao buscar unidades do sessionStorage:', error);
            }
          }

          const hotelName = hotelUnit ? hotelUnit.label : forecast.unidade_id;

          return {
            id: forecast.id,
            nome: hotelName,
            mes: forecast.mes,
            ano: forecast.ano,
            publicado: true,
            versao: forecast.version,
            dados: {
              ocupacao: formatarValor(forecast.TO, 'porcentagemFormat'),
              diariaMedia: formatarValor(forecast.ADR, 'moeda'),
              revPar: formatarValor(forecast.REVPARSCAFE, 'moeda'),
              receita: formatarValor(forecast.receitaHospedagem, 'moeda'),
            },
            detalhes: {
              disponivel: forecast.disponivel,
              aptOcc: forecast.aptOcc,
              updatedAt: new Date(forecast.updatedAt).toLocaleDateString('pt-BR'),
            },
          };
        });

        setHotels(mappedHotels);
      } else {
        setHotels([]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setHotels([]);
    } finally {
      dispatch({
        type: 'OPEN_LOADING',
        text: 'Carregando dados...',
        isLoading: false,
      });
    }
  };

  useEffect(() => {
    try {
      const unidadesFromStorage = sessionStorage.getItem('unidades');

      if (unidadesFromStorage) {
        const unidades = JSON.parse(unidadesFromStorage);

        const options = [
          { label: 'Todas as unidades', value: '' },
          ...unidades
            .filter((unit: any) => unit.uhs != null)
            .map((unit: any) => ({
              label: unit.unidade,
              value: unit.unidade_id,
            })),
        ];

        setUnitOptions(options);
        setSelectedUnit(options[0]);

        loadData();
      }
    } catch (error) {
      console.error('Error loading units from sessionStorage:', error);
    }
  }, []);

  const handleUnitChange = (_event: React.SyntheticEvent, newValue: UnitOption | null) => {
    setSelectedUnit(newValue);
    setFilters((prev) => ({
      ...prev,
      unidade_id: newValue?.value || '',
    }));
  };

  const handleApplyFilters = (): void => {
    loadData();
  };

  const getMonthName = (month: number): string => {
    const date = new Date();
    date.setMonth(month - 1);
    return date.toLocaleString('pt-BR', { month: 'long' });
  };

  const handleSaveForecast = async (forecastData: ForecastData): Promise<void> => {
    try {
      dispatch({
        type: 'OPEN_LOADING',
        text: 'Salvando forecast...',
        isLoading: true,
      });

      const apiData = {
        unidade_id: forecastData.hotelId,
        disponivel: forecastData.disp,
        aptOcc: forecastData.aptsOcupados,
        ADR: forecastData.diariaMedia,
        receitaHospedagem: forecastData.receitaHospedagem,
        ano: forecastData.year,
        mes: forecastData.month,
        usuario_id: cookies.usuario?.id,
      };

      const response = await createForecast(cookies, apiData);
      setShowForecastModal(false);
      dispatch({
        type: 'SHOW_MESSAGE',
        message: response.mensagem || 'Forecast salvo com sucesso!',
        variant: 'success',
      });

      loadData();
    } catch (error) {
      console.error('Erro ao salvar forecast:', error);
      dispatch({
        type: 'SHOW_MESSAGE',
        message: 'Erro ao salvar forecast. Tente novamente.',
        variant: 'error',
      });
    } finally {
      dispatch({
        type: 'OPEN_LOADING',
        text: 'Salvando forecast...',
        isLoading: false,
      });
    }
  };

  return (
    <PageContainer>
      <div className="title-container">
        <TitleContainer>
          <>
            <div className="icon">
              <BiTargetLock />
            </div>
            <div className="text">Lançamento Forecast</div>
          </>
        </TitleContainer>
      </div>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
        }}
      >
        <Box onClick={() => setShowFilters(!showFilters)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <BiFilterAlt style={{ marginRight: '8px' }} /> Filtros
          </Typography>
          <IconButton size="small" onClick={() => setShowFilters(!showFilters)} sx={{ width: '32px', height: '32px' }}>
            {showFilters ? <BiChevronUp /> : <BiChevronDown />}
          </IconButton>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                value={selectedUnit}
                onChange={handleUnitChange}
                options={unitOptions}
                renderInput={(params) => <TextField {...params} label="Unidade" placeholder="Selecione uma unidade" fullWidth />}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField label="Ano" type="number" fullWidth value={filters.year} onChange={(e) => setFilters((prev) => ({ ...prev, year: Number(e.target.value) }))} />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="month-select-label">Mês</InputLabel>
                <Select labelId="month-select-label" value={filters.month} label="Mês" onChange={(e) => setFilters((prev) => ({ ...prev, month: Number(e.target.value) }))}>
                  {meses.map((mes) => (
                    <MenuItem key={mes.value} value={mes.value}>
                      {mes.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="primary" onClick={handleApplyFilters} sx={{ mt: 1 }}>
                Aplicar Filtros
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      <AccordionContainer>
        {hotels.length > 0 ? (
          hotels.map((hotel) => (
            <div key={hotel.id}>
              <HotelHeader onClick={() => toggleAccordion(hotel.id)}>
                <HotelName>
                  <div className="icon">{activeKeys.includes(hotel.id) ? <BiChevronUp /> : <BiChevronDown />}</div>
                  <div>{hotel.nome}</div>
                </HotelName>
                <MetaInfo>
                  {getMonthName(hotel.mes)}/{hotel.ano} - v{hotel.versao}
                  {hotel.publicado && (
                    <div className="published-badge">
                      <span>Publicado</span>
                    </div>
                  )}
                </MetaInfo>
              </HotelHeader>

              {activeKeys.includes(hotel.id) && (
                <AccordionContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Receita
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Diária Média:</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {hotel.dados.diariaMedia}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">RevPAR:</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {hotel.dados.revPar}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Receita:</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {hotel.dados.receita}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                          Ocupação
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Aptos. Disponíveis:</Typography>
                          <Typography variant="body1">{hotel.detalhes?.disponivel || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Aptos. Ocupados:</Typography>
                          <Typography variant="body1">{hotel.detalhes?.aptOcc || '-'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body1">Ocupação:</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {hotel.dados.ocupacao}
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Última atualização: {hotel.detalhes?.updatedAt || '-'}
                    </Typography>
                  </Box>
                </AccordionContent>
              )}
            </div>
          ))
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px dashed #dee2e6',
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Nenhum forecast encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Não foram encontrados forecasts para os filtros selecionados.
              <br />
              Tente ajustar os filtros ou criar um novo forecast.
            </Typography>
            <Button variant="contained" color="primary" startIcon={<BiPlus />} onClick={() => setShowForecastModal(true)}>
              Novo Forecast
            </Button>
          </Paper>
        )}
      </AccordionContainer>

      <ForecastModal open={showForecastModal} onClose={() => setShowForecastModal(false)} onSave={handleSaveForecast} cookies={cookies} />

      <Fab icon={<BiPlus />} alwaysShowTitle={true} mainButtonStyles={{ backgroundColor: '#17a2b8' }} style={{ bottom: 0, right: 0, zoom: '80%', margin: '20px' }}>
        <Action text={'Novo Forecast'} style={{ backgroundColor: '#28a745' }} onClick={() => setShowForecastModal(true)}>
          <BiPlus />
        </Action>
      </Fab>
    </PageContainer>
  );
};

export default LancamentoForecast;
